import { Component, EventEmitter, Input, Output } from '@angular/core';
import { RichTextEditorComponent } from '../fields/rich-text-editor/rich-text-editor.component';
import { ControlContainer, FormControl, FormGroupDirective, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { UploadAttachmentsComponent } from '../fields/upload-attachments/upload-attachments.component';
import { SelectFormFieldComponent } from '../fields/select-form-field/select-form-field.component';
import { InputFormFieldComponent } from '../fields/input-form-field/input-form-field.component';
import { DatepickerFormFieldComponent } from '../fields/datepicker-form-field/datepicker-form-field.component';
import { CheckboxFormFieldComponent } from '../fields/checkbox-form-field/checkbox-form-field.component';
import { TextareaFormFieldComponent } from '../fields/textarea-form-field/textarea-form-field.component';
import { ConfigurationService } from 'app/shared/services/configuration.service';
import { take } from 'rxjs';
import { DropdownEntryEnum, DropdownTypeEnum } from 'app/shared/enums/dropdowns.enum';
import { CommonModule } from '@angular/common';
import { ProblemRecordsFormService } from 'app/problem-records-form/problem-records-form.service';
import { ConfigurationSelectFormFieldComponent } from '../fields/configuration-select-form-field/configuration-select-form-field.component';
import { CaseTypeEnum } from 'app/shared/enums/case-type.enum';
import { HelperService } from 'app/shared/services/helper.service';

@Component({
  selector: 'app-problem-record-problem-details-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RichTextEditorComponent,
    UploadAttachmentsComponent,
    SelectFormFieldComponent,
    InputFormFieldComponent,
    DatepickerFormFieldComponent,
    CheckboxFormFieldComponent,
    TextareaFormFieldComponent,
    TranslateModule,
    ConfigurationSelectFormFieldComponent
  ],
  templateUrl: './problem-record-problem-details-form.component.html',
  styleUrl: './problem-record-problem-details-form.component.scss',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class ProblemRecordProblemDetailsFormComponent {
  @Input() attachmentsTouched!: boolean;
  @Input() viewMode!: boolean;
  @Input() caseId!: number;
  @Input() productFamilyItems: any[] = [];
  @Input() problemPriorityItems: any[] = [];
  @Input() stageItems: any[] = [];
  @Input() uploadedFiles: any[] = [];
  caseType: string = CaseTypeEnum.PROBLEM_RECORD;
  productGroupItems: any[] = [];
  productSubGroupItems: any[] = [];

  form: any;
  dropdownTypeEnum = DropdownTypeEnum;
  dropdownType = DropdownTypeEnum.PRODUCT_INFORMATION;
  dropdownEquipmentStatusType = DropdownTypeEnum.EQUIPMENT_STATUS;
  entry1 = DropdownEntryEnum.ENTRY_1;
  entry2 = DropdownEntryEnum.ENTRY_2;
  entry3 = DropdownEntryEnum.ENTRY_3;
  entry4 = DropdownEntryEnum.ENTRY_4;
  entry5 = DropdownEntryEnum.ENTRY_5;

  constructor(
    public mainForm: FormGroupDirective,
    public configurationService: ConfigurationService,
    private problemRecordsFormService: ProblemRecordsFormService,
    private helperService: HelperService
  ) { }

  ngOnInit(): void {
    this.createForm();
  }

  createForm() {
    this.form = this.mainForm.form;

    this.form.addControl('problemTitle', new FormControl({ value: null, disabled: this.viewMode }, [Validators.required]));
    this.form.addControl('problemPriority', new FormControl(null, [Validators.required]));
    this.form.addControl('productFamily', new FormControl(null, [Validators.required]));
    this.form.addControl('productGroup', new FormControl({ value: null, disabled: !this.viewMode }, [Validators.required]));
    this.form.addControl('productSubGroup', new FormControl({ value: null, disabled: !this.viewMode }, [Validators.required]));
    this.form.addControl('productSuite', new FormControl({ value: null, disabled: true }, []));
    this.form.addControl('productDetails', new FormControl(null, []));
    this.form.addControl('problemStage', new FormControl(null, [Validators.required]));
    this.form.addControl('productCompany', new FormControl({ value: null, disabled: true }, []));
    this.form.addControl('problemDescription', new FormControl(null, [Validators.required]));
    this.form.addControl('containmentSummary', new FormControl(null, []));
    this.form.addControl('resolutionSummary', new FormControl(null, []));
    this.form.addControl('attachments', new FormControl([]));
  }

  onFileUpload(attachments: any[]) {
    this.form.get('attachments')?.setValue([...attachments]);
    this.form.get('attachments')?.updateValueAndValidity();
    this.form.get('attachments')?.markAsDirty();
  }

  onSelectProductFields(data: any, field: string) {
    this.resetProductFields(field, data);

    if(!data) {
      return;
    }

    let params = {
      type: this.dropdownType,
      entry1: this.form.get('productFamily')?.value || '',
      entry2: this.form.get('productGroup')?.value || '',
      entry3: this.form.get('productSubGroup')?.value || '',
    };

    if(!params.entry1) {
      return;
    }

    let entry = this.entry2;

    if(field === DropdownTypeEnum.PRODUCT_SUB_GROUP) {
      if(!params.entry2) {
        return;
      }
      entry = this.entry3;
    }

    this.configurationService.getDropdownConfigurations(params, entry)
    .pipe(take(1))
    .subscribe((res: any)=> {
      switch (field) {
        case DropdownTypeEnum.PRODUCT_GROUP:
          this.productGroupItems = res;
          break;
        case DropdownTypeEnum.PRODUCT_SUB_GROUP:
          this.productSubGroupItems = res;
        break;
      }
    });

  }

  onSelectProductSubGroupField(data: any) {
    if(!data) {
      return;
    }

    this.resetProductSuiteProductCompany();

    const productCompanyParams = {
      type: this.dropdownType,
      entry1: this.form.get('productFamily')?.value || '',
      entry2: this.form.get('productGroup')?.value || '',
      entry3: this.form.get('productSubGroup')?.value || '',
      entry4: ''
    };

    this.configurationService.getSingleDropdownConfigurations(productCompanyParams)
    .pipe(take(1))
    .subscribe((res: any)=> {
      this.form.get('productCompany')?.setValue(res ? res.entry4 : null);
      this.form.get('productSuite')?.setValue(res ? res.entry5 : null);
    });

  }

  resetProductSuiteProductCompany() {
    this.form.get('productSuite')?.setValue(null);
    this.form.get('productCompany')?.setValue(null);

    this.problemRecordsFormService.resetProblemRecordCasesEmiter.emit(true);
  }

  resetProductFields(resetField: string, data: any) {
    if(resetField === DropdownTypeEnum.PRODUCT_GROUP) {
      this.form.get('productGroup').setValue(null);
      this.productGroupItems = [];
      this.helperService.enableDisableDependentFieldOnSelect(this.form, data, 'productGroup');
      
      this.resetProductFields(DropdownTypeEnum.PRODUCT_SUB_GROUP, null);
    }

    if (resetField === DropdownTypeEnum.PRODUCT_SUB_GROUP) {
      this.form.get('productSubGroup').setValue(null);
      this.productSubGroupItems = [];
      this.helperService.enableDisableDependentFieldOnSelect(this.form, data, 'productSubGroup');
    }

    this.resetProductSuiteProductCompany();

  }

}
